import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { isEmpty, map } from "lodash";
import image from "../../assets/logo/authLogo.png";
import appConfig from "../config.json";
import { centrePdfText } from "../helpers/app/pdf";
import { numberWithCommas } from "../helpers/app/numberFormater";
import { toBulletText } from "../../components/common/BulletTextArea";
import {
  conceptNoteTextFields,
  invitedParticipantColumns,
  INVITED_PARTICIPANTS_LABEL,
} from "../../containers/programsMgt/activityConceptNotes/mangeConceptNotes/conceptNoteFields";

const fullName = (user) =>
  user ? `${user.first_name || ""} ${user.other_names || ""}`.trim() : "";

const formatDate = (date) => (date ? moment(date).format("DD/MM/YYYY") : "");

const emptyRow = (text, colSpan) => [
  { content: text, colSpan, styles: { halign: "center", textColor: 120 } },
];

const loadImage = async (path) => {
  if (!path) return null;
  try {
    const response = await fetch(`${appConfig.server_url}${path}`);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const tableDefaults = {
  theme: "grid",
  margin: { left: 14, right: 14 },
  styles: {
    fontSize: 9,
    cellPadding: 2,
    overflow: "linebreak",
    valign: "top",
    textColor: 20,
  },
  headStyles: { fillColor: [41, 98, 155], textColor: 255, fontStyle: "bold" },
};

const sectionTitleStyle = { fontStyle: "bold", fillColor: [235, 240, 245] };

const generateActivityConceptNotePdf = async (data) => {
  const doc = new jsPDF();
  const nextY = () => doc.lastAutoTable.finalY + 8;

  doc.setFontSize(10);
  doc.addImage(image, "PNG", 60, 15, 80, 25);
  centrePdfText(
    doc,
    " P.O.BOX 610029, Maracha, Uganda |Adongoro Cell, Maracha Town Council",
    45
  );
  centrePdfText(doc, "Mob: +256 393102379", 50);
  centrePdfText(
    doc,
    "Web: www.amaniinitiative.org  | Email: info.amaniinitiative@gmail.com  ",
    55
  );
  doc.setFontSize(12);
  centrePdfText(doc, "AMANI INITIATIVE ACTIVITY CONCEPT NOTE", 65);
  doc.setFontSize(10);

  doc.autoTable({
    ...tableDefaults,
    startY: 70,
    head: [["SUB-TITLES", "DESCRIPTION"]],
    body: [
      ["Project Name", data?.project?.name || ""],
      ["Funder", data?.project?.donors?.name || ""],
      ["Budget Line", data?.budgetLine?.name || ""],
      ["Strategic Plan Priority Input", data?.strategicPlan?.title || ""],
      ["Department/Thematic Area", data?.thematicArea?.thematic_area || ""],
      ["Activity Name", data?.activity?.activity || ""],
      ["Activity Dates", formatDate(data?.date)],
      ...map(conceptNoteTextFields, (field) => [
        field.label,
        field.bulleted
          ? toBulletText(data?.[field.name])
          : data?.[field.name] || "",
      ]),
    ],
    columnStyles: { 0: { cellWidth: 55, ...sectionTitleStyle } },
  });

  const participants = data?.invited_participants || [];
  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [
      [
        {
          content: INVITED_PARTICIPANTS_LABEL,
          colSpan: invitedParticipantColumns.length,
        },
      ],
      map(invitedParticipantColumns, "label"),
    ],
    body: isEmpty(participants)
      ? [emptyRow("No participants listed", invitedParticipantColumns.length)]
      : map(participants, (participant) =>
          map(
            invitedParticipantColumns,
            (column) => participant?.[column.name] || ""
          )
        ),
  });

  const sessions = data?.activities || [];
  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [
      [
        {
          content: "Annex 1: Proposed Detailed Activity Agenda or Program",
          colSpan: 4,
        },
      ],
      ["Session", "Person Responsible", "Start", "End"],
    ],
    body: isEmpty(sessions)
      ? [emptyRow("No sessions added", 4)]
      : map(sessions, (session) => [
          session.session || "",
          session.person_responsible || "",
          session.start ? moment(session.start).format("DD/MM/YYYY HH:mm") : "",
          session.end ? moment(session.end).format("DD/MM/YYYY HH:mm") : "",
        ]),
  });

  const costDrivers = data?.requisitionCostDrivers || [];
  const totalCost = costDrivers.reduce(
    (acc, item) => acc + (item.total_cost || 0),
    0
  );
  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [
      [
        { content: "Annex 2: Proposed Detailed Activity Budget", colSpan: 4 },
      ],
      ["Budget Item", "Units", "Unit Cost", "Total Cost"],
    ],
    body: isEmpty(costDrivers)
      ? [emptyRow("No budget items (no requisition raised yet)", 4)]
      : [
          ...map(costDrivers, (item) => [
            item.name || "",
            numberWithCommas(item.units ?? 0),
            `UGX ${numberWithCommas(item.unit_cost ?? 0)}`,
            `UGX ${numberWithCommas(item.total_cost ?? 0)}`,
          ]),
          [
            { content: "Total", styles: { fontStyle: "bold" } },
            "",
            "",
            {
              content: `UGX ${numberWithCommas(totalCost)}`,
              styles: { fontStyle: "bold" },
            },
          ],
        ],
  });

  const signatories = [
    ["Prepared By", data?.created_by, data?.created_at],
    ["Checked By", data?.checked_by, data?.checked_at],
    ["Approved By", data?.approved_by, data?.approved_at],
  ];
  const signatures = await Promise.all(
    map(signatories, ([, user]) => loadImage(user?.signature))
  );

  doc.autoTable({
    ...tableDefaults,
    startY: nextY(),
    head: [["", "Name", "Signature", "Date"]],
    body: map(signatories, ([label, user, date]) => [
      label,
      fullName(user),
      "",
      user ? formatDate(date) : "",
    ]),
    columnStyles: {
      0: { cellWidth: 35, ...sectionTitleStyle },
      2: { cellWidth: 45, minCellHeight: 14 },
    },
    didDrawCell: (hook) => {
      const signature = signatures[hook.row.index];
      if (hook.section !== "body" || hook.column.index !== 2 || !signature)
        return;
      try {
        doc.addImage(
          signature,
          hook.cell.x + 2,
          hook.cell.y + 1,
          hook.cell.width - 4,
          hook.cell.height - 2
        );
      } catch {
      }
    },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.text(
      `Page ${page} of ${pageCount}`,
      doc.internal.pageSize.width - 14,
      doc.internal.pageSize.height - 8,
      { align: "right" }
    );
  }

  doc.save(`ACTIVITY CONCEPT NOTE - ${data?.title || ""}.pdf`);
};

export default generateActivityConceptNotePdf;
